import { useMemo } from "react";

/**
 * Compute the parameters used in the current scenario.
 *
 * @param {Object} params
 * @param {Object} params.integrations   – Ex. { shopify: true, klaviyo: false }
 * @param {Object} params.scenario       – Should contain scenario.campaign?.automationType
 * @param {Object} params.user           – Should contain user.customer?.customFields
 *
 * @returns {Object} { paramOptions, imageParamOptions }
 */
const useParameters = ({ integrations = {}, scenario = {}, user = {} }) => {
  const paramOptions = useMemo(() => {
    let parameters = integrations.shopify
      ? [
          { label: "First name (Shopify)", value: "shopifyCustomer.firstname" },
          { label: "WhatsApp Name", value: "contact.whatsappName" },
          { label: "Phone Number", value: "contact.userPhoneNumber" },
        ]
      : [
          { label: "First Name", value: "firstName" },
          { label: "Last Name", value: "lastName" },
          { label: "WhatsApp Name", value: "contact.whatsappName" },
          { label: "Phone Number", value: "contact.userPhoneNumber" },
        ];

    if (integrations.shopify && !integrations.klaviyo) {
      parameters = [
        ...parameters,
        { label: "First Name (from imports)", value: "firstName" },
      ];
    }

    const automationType = scenario.campaign?.automationType;

    if (
      ["back-in-stock", "abandoned-cart", "just-abandoned-checkout"].includes(
        automationType,
      )
    ) {
      parameters = [
        ...parameters,
        { label: "Product name (Shopify)", value: "shopifyProduct.title" },
      ];
    } else {
      if (["order-tracking", "order-confirmation"].includes(automationType)) {
        parameters = [
          ...parameters,
          { label: "Order number (Shopify)", value: "shopifyOrder.name" },
          {
            label: "Order status URL (Shopify)",
            value: "shopifyOrder.orderStatusUrl",
          },
          { label: "Product name (Shopify)", value: "shopifyProduct.title" },
        ];
      }
    }

    if (automationType === "order-tracking") {
      parameters = [
        ...parameters,
        {
          label: "Order tracking URL (Shopify)",
          value: "shopifyFulfillment.trackingUrl",
        },
        {
          label: "Tracking company (Shopify)",
          value: "shopifyFulfillment.trackingCompany",
        },
        {
          label: "Tracking number (Shopify)",
          value: "shopifyFulfillment.trackingNumber",
        },
      ];
    }

    if (user?.customer?.customFields) {
      const customFieldOptions = Object.keys(user.customer.customFields).map(
        (fieldKey) => {
          return {
            label: `Custom field: ${fieldKey}`,
            value: fieldKey,
          };
        },
      );

      if (customFieldOptions.length > 0) {
        parameters = [...parameters, ...customFieldOptions];
      }
    }

    return parameters;
  }, [integrations, scenario, user]);

  const imageParamOptions = useMemo(() => {
    const automationType = scenario.campaign?.automationType;

    if (
      integrations.shopify &&
      [
        "back-in-stock",
        "abandoned-cart",
        "just-abandoned-checkout",
        "order-tracking",
        "order-confirmation",
      ].includes(automationType)
    ) {
      return [
        { label: "Product image (Shopify)", value: "shopifyProductImage" },
        { label: "Custom image", value: "custom" },
      ];
    }

    return [{ label: "Custom image", value: "custom" }];
  }, [integrations, scenario]);

  return { paramOptions, imageParamOptions };
};

export default useParameters;
